
import Vue from "vue";
import TextField from "@/components/molecules/TextField.vue";
import PaymentDescription from "@/components/organisms/subscription/PaymentDescription.vue";
import SiteFooter from "@/components/organisms/Footer.vue";
import CatIcon from "@/components/atoms/CatIcon.vue";

import { Auth } from "aws-amplify";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/SimpleDialog.vue";
import UserDeleteDialog, { UserDeleteDialogProps } from "@/components/ui/UserDeleteDialog.vue";
import AssistantCatSelectionDialog from "@/components/ui/AssistantCatSelectionDialog.vue";
import CatNicknameInputDialog from "@/components/ui/CatNicknameInputDialog.vue";
import CatNicknameConfirmDialog from "@/components/ui/CatNicknameConfirmDialog.vue";
import CatNicknameSuccessDialog from "@/components/ui/CatNicknameSuccessDialog.vue";
import FeedbackManuscriptModeSelectionDialog from "@/components/ui/FeedbackManuscriptModeSelectionDialog.vue";
import WriterTypeIndicatorSkipConfirmDialog from "@/components/ui/WriterTypeIndicatorSkipConfirmDialog.vue";
import { UserClient } from "@/lib/clients";
import { User, Plan, FeedbackManuscriptMode } from "@/lib/models";
import { AssistantCatMaster, AssistantCat } from "@/lib/models/assistantCat";
import { db } from "@/lib/indexeddb";
import { clearStorageKeepingLocale, isJapaneseRegion } from "@/lib/utils/i18n/locale";
import { mapGetters, mapActions } from "vuex";
import PlusIcon from "icons/Plus.vue";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import { AppTheme } from "@/store/modules/general";

const MODE_TITLE_KEYS: Record<FeedbackManuscriptMode, string> = {
  [FeedbackManuscriptMode.NORMAL]: "myPage.assistantCat.modeNames.normal",
  [FeedbackManuscriptMode.DRY]: "myPage.assistantCat.modeNames.dry",
};

const userClient = new UserClient();

export default Vue.extend<Data, Methods, Computed, {}>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    TextField,
    PaymentDescription,
    SiteFooter,
    PlusIcon,
    CatIcon,
    AssistantCatSelectionDialog,
    CatNicknameInputDialog,
    CatNicknameConfirmDialog,
    CatNicknameSuccessDialog,
    FeedbackManuscriptModeSelectionDialog,
    WriterTypeIndicatorSkipConfirmDialog,
  },
  data() {
    return {
      id: "",
      email: "",
      userName: null,
      loaded: false,
      isProcessing: false,
      showAssistantCatDialog: false,
      showFeedbackManuscriptModeDialog: false,
      showSkipConfirmDialog: false,
      assistantCatMaster: null as AssistantCatMaster | null,
      assistantCats: [] as AssistantCat[],
      // ニックネーム変更ダイアログの状態管理
      showNicknameInputDialog: false,
      showNicknameConfirmDialog: false,
      showNicknameSuccessDialog: false,
      pendingNickname: "",
      nicknameReactionMessage: "",
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },
  computed: {
    ...mapGetters("fishModule", [
      "formattedCurrentFish",
      "formattedPrepaidFish",
      "formattedFreeFish",
      "isLoading",
      "error",
    ]),
    ...mapGetters("billingModule", ["hasPaymentError"]),
    changed() {
      if (this.user) {
        return this.userName !== this.user.name;
      }
      return false;
    },
    user() {
      const user: User = this.$store.getters["userModule/user"];
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.userName = user && user.name;
      return user;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFreePlan() {
      return this.plan === Plan.free;
    },
    isSchoolPlan() {
      return this.plan === Plan.school;
    },
    fishLoading() {
      return this.$store.getters["fishModule/isLoading"];
    },
    fishError() {
      return this.$store.getters["fishModule/error"];
    },
    hasWriterTypeResult() {
      // assistantCatMasterがnullでない場合は作家タイプ診断済み
      return this.assistantCatMaster !== null;
    },
    selectedCatType() {
      return this.assistantCatMaster?.selectedType || null;
    },
    selectedCatNickname() {
      const selectedCat = this.assistantCats.find((cat: AssistantCat) => cat.selected);
      return selectedCat?.name || "";
    },
    selectedCatAssistantType() {
      const selectedCat = this.assistantCats.find((cat: AssistantCat) => cat.selected);
      return selectedCat?.assistantCatType || null;
    },
    selectedAssistantCat() {
      return this.assistantCats.find((cat: AssistantCat) => cat.selected) || null;
    },
    feedbackManuscriptMode(): string {
      const mode = this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
      const key = MODE_TITLE_KEYS[mode];
      return this.$t(key) as string;
    },
    inputDialogInitialNickname() {
      // キャンセルで戻った場合はpendingNickname、初回表示時はselectedCatNickname
      return this.pendingNickname || this.selectedCatNickname;
    },
    isJapaneseRegion() {
      return isJapaneseRegion();
    },
    isResettingNicknameToDefault() {
      return this.pendingNickname === "";
    },
    appTheme() {
      return this.$store.getters["generalModule/appTheme"];
    },
  },
  methods: {
    ...mapActions("writerTypeResultModule", ["fetchMyResult"]),
    onChangeTheme(event: Event) {
      const target = event.target as HTMLSelectElement;
      const theme = target.value as AppTheme;
      this.$store.dispatch("generalModule/setAppTheme", theme);
    },
    async logout() {
      const result = await Auth.signOut();
      if (!result) {
        clearStorageKeepingLocale(); // 言語設定を保持しながらlocalStorageをクリア
      }
      window.sessionStorage.removeItem("vuex"); // VuexのStoreを初期化
      db.delete(); // IndexedDBの初期化
      // 初期化したDBへの次の接続を担保するためにリロードを走らせる
      window.location.href = "/";
    },
    async save() {
      const { userName } = this;
      this.$store.dispatch("userModule/updateUser", userName);
    },
    async onClickDeleteAccount() {
      this.isProcessing = true;

      const results = await userClient.checkConnectedOtherServices();

      this.isProcessing = false;

      if (results.nolaPublishing.error || results.nolaNovel.error) {
        let errorMessage = "";
        if (results.nolaNovel.error) {
          errorMessage += `${this.$t("myPage.deleteDialog.errorLabelNolaNovel")}${results.nolaNovel.error}`;
        }
        if (results.nolaPublishing.error) {
          errorMessage += errorMessage && "\n";
          errorMessage += `${this.$t("myPage.deleteDialog.errorLabelNolaPublishing")}${results.nolaPublishing.error}`;
        }
        const warningDialog = new Dialog(SimpleDialog);
        const options: SimpleDialogProps = {
          title: this.$t("myPage.deleteDialog.cannotDeleteTitle") as string,
          content: `${this.$t("myPage.deleteDialog.cannotDeleteMessage")}${errorMessage}`,
        };
        warningDialog.show(options);
        return;
      }

      const confirmDialog = new Dialog(UserDeleteDialog);
      const data: UserDeleteDialogProps = {
        existsNolaPublishingUser: results.nolaPublishing.result!,
        existsNolaNovelUser: results.nolaNovel.result!,
        positive: this.$t("common.delete") as string,
        positiveCallback: async () => this.deleteUser(),
      };
      confirmDialog.show(data);
    },
    async deleteUser() {
      this.$store.dispatch("userModule/deleteUser", {
        email: this.email,
        callback: async () => {
          await Auth.signOut();
          window.localStorage.clear();
          window.sessionStorage.removeItem("vuex"); // VuexのStoreを初期化
          db.delete(); // IndexedDBの初期化
          // 初期化したDBへの次の接続を担保するためにリロードを走らせる
          window.location.href = "/accountDeleted";
        },
      });
    },
    async onClickPurchase() {
      // PurchaseFishページに遷移する
      this.$router.push({ name: "purchaseFish" });
    },
    async onClickUpgrade() {
      // プレミアム登録ページに遷移する
      this.$router.push({ name: "subscriptionAnnounce" });
    },
    async retryFetchFishData() {
      await this.$store.dispatch("fishModule/fetchFishData");
    },
    onClickChangeAssistantCat() {
      this.showAssistantCatDialog = true;
    },
    onClickChangeFeedbackManuscriptMode() {
      this.showFeedbackManuscriptModeDialog = true;
    },
    async onCloseAssistantCatDialog() {
      this.showAssistantCatDialog = false;
      // ダイアログ閉じた後にアシスタント猫データを再取得（変更が反映される）
      if (this.hasWriterTypeResult) {
        await this.fetchAssistantCats();
        // CatIconに反映するためにassistantCatMasterも更新
        await this.fetchAssistantCatMaster();
      }
    },
    async onCloseFeedbackManuscriptModeDialog() {
      this.showFeedbackManuscriptModeDialog = false;
      // CatIconに反映するためにassistantCatMasterも更新
      await this.fetchAssistantCatMaster();
    },
    onClickChangeNickname() {
      // 入力ダイアログを開く際にpendingNicknameをリセット
      this.pendingNickname = "";
      this.showNicknameInputDialog = true;
    },
    onSaveNickname(nickname: string) {
      this.pendingNickname = nickname;

      this.showNicknameInputDialog = false;
      this.showNicknameConfirmDialog = true;
    },
    onCancelNicknameInput() {
      this.showNicknameInputDialog = false;
    },
    async onConfirmNickname() {
      if (!this.selectedCatAssistantType) {
        return;
      }

      try {
        await this.$store.dispatch("assistantCatModule/updateCatNickname", {
          assistantCatType: this.selectedCatAssistantType,
          name: this.pendingNickname,
        });

        // 成功時はアシスタント猫一覧を再取得して最新データを反映
        await this.fetchAssistantCats();

        // GAイベントを発火
        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeNickname, this.selectedCatAssistantType);

        // リアクションメッセージを設定（再取得後のselected: trueの猫のreactionAfterSetNameを使用）
        this.nicknameReactionMessage = this.selectedAssistantCat?.reactionAfterSetName || "";

        // 確認ダイアログを閉じて成功ダイアログを表示
        this.showNicknameConfirmDialog = false;
        this.showNicknameSuccessDialog = true;
      } catch (error) {
        console.error("Failed to change nickname:", error);
        this.showNicknameSuccessDialog = false;
        this.showNicknameConfirmDialog = true;
        this.$notify({
          title: this.$t("myPage.assistantCat.nicknameChangeError") as string,
          type: "error",
        });
      }
    },
    onCloseNicknameConfirm() {
      this.showNicknameConfirmDialog = false;
    },
    onOkNicknameSuccess() {
      this.showNicknameSuccessDialog = false;
      this.pendingNickname = "";
      this.nicknameReactionMessage = "";
    },
    onClickWriterTypeIndicator() {
      const baseUrl = process.env.VUE_APP_NOLA_WEB;
      const writerTypeUrl = `${baseUrl}/writer-type-indicator/`;
      window.open(writerTypeUrl, "_blank");
    },
    onClickSkipWriterType() {
      this.showSkipConfirmDialog = true;
    },
    async onSkipConfirmDone() {
      this.showSkipConfirmDialog = false;
      await this.fetchAssistantCatMaster();
      await this.fetchAssistantCats();
    },
    async fetchAssistantCatMaster() {
      try {
        this.assistantCatMaster = await this.$store.dispatch("assistantCatModule/fetchAssistantCatMaster");
      } catch (error) {
        console.error("Failed to fetch assistant cat master:", error);
        // エラー時はnullのままにして、作家タイプ診断未受診として扱う
        this.assistantCatMaster = null;
      }
    },
    async fetchAssistantCats() {
      try {
        this.assistantCats = await this.$store.dispatch("assistantCatModule/fetchAssistantCatList");
      } catch (error) {
        console.error("Failed to fetch assistant cat list:", error);
        // エラー時は空配列のままにする
        this.assistantCats = [];
      }
    },
  },
  async created() {
    const user = await Auth.currentUserPoolUser().catch((err) => null);
    if (user) {
      this.id = user.signInUserSession.idToken.payload.sub;

      const email = user.attributes.email || user.username;
      this.email = email;
    }

    await Promise.all([
      this.$store.dispatch("userModule/initialize"),
      this.$store.dispatch("billingModule/fetchBillingInfo"),
      this.fetchMyResult(), // 作家タイプ診断の結果を取得
      this.fetchAssistantCatMaster(), // アシスタント猫マスタの取得
    ]);

    // 作家タイプ診断済みの場合のみアシスタント猫一覧を取得
    if (this.hasWriterTypeResult) {
      await this.fetchAssistantCats();
    }

    this.loaded = true;
  },
  mounted() {
    this.$store.dispatch("fishModule/fetchFishData");
  },
});

interface Data {
  id: string;
  email: string;
  userName: string | null;
  loaded: boolean;
  isProcessing: boolean;
  showAssistantCatDialog: boolean;
  showFeedbackManuscriptModeDialog: boolean;
  showSkipConfirmDialog: boolean;
  assistantCatMaster: AssistantCatMaster | null;
  assistantCats: AssistantCat[];
  showNicknameInputDialog: boolean;
  showNicknameConfirmDialog: boolean;
  showNicknameSuccessDialog: boolean;
  pendingNickname: string;
  nicknameReactionMessage: string;
  nolaAnalytics: NolaAnalytics;
}

interface Computed {
  changed: boolean;
  user: User;
  plan: Plan;
  isFreePlan: boolean;
  isSchoolPlan: boolean;
  formattedCurrentFish: string;
  formattedPrepaidFish: string;
  formattedFreeFish: string;
  fishLoading: boolean;
  fishError: string | null;
  hasPaymentError: boolean;
  hasWriterTypeResult: boolean;
  selectedCatType: string | null;
  selectedCatNickname: string;
  selectedCatAssistantType: string | null;
  selectedAssistantCat: AssistantCat | null;
  inputDialogInitialNickname: string;
  isJapaneseRegion: boolean;
  isResettingNicknameToDefault: boolean;
  appTheme: AppTheme;
  feedbackManuscriptMode: string;
}

interface Methods {
  logout: () => void;
  save: () => void;
  onClickDeleteAccount: () => void;
  deleteUser: () => void;
  onClickPurchase: () => void;
  onClickUpgrade: () => void;
  retryFetchFishData: () => void;
  onClickChangeAssistantCat: () => void;
  onClickChangeFeedbackManuscriptMode: () => void;
  onCloseAssistantCatDialog: () => void;
  onCloseFeedbackManuscriptModeDialog: () => void;
  onClickChangeNickname: () => void;
  onSaveNickname: (nickname: string) => void;
  onCancelNicknameInput: () => void;
  onConfirmNickname: () => Promise<void>;
  onCloseNicknameConfirm: () => void;
  onOkNicknameSuccess: () => void;
  onClickWriterTypeIndicator: () => void;
  onClickSkipWriterType: () => void;
  onSkipConfirmDone: () => Promise<void>;
  fetchMyResult: () => Promise<void>;
  fetchAssistantCatMaster: () => Promise<void>;
  fetchAssistantCats: () => Promise<void>;
  onChangeTheme: (event: Event) => void;
}
